import { useWorkflowContext } from 'app-context/worflow/WorkflowContext';
import RightSlider from 'component/hoc/rightSlider/RightSlider';
import CaptureForm from 'component/organism/workflow/forms/capture/CaptureForm';
import { FormikProvider, useFormik } from 'formik';
import { isEqual } from 'lodash';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import styles from './CaptureModal.module.scss';

const CaptureModal = ({
  isOpen,
  data,
  onClose,
  title,
  id,
  isFetching,
  isError,
}) => {
  const {
    workflowData,
    setWorkflowData,
    selectedNode,
    initialWorkflowData,
    setIsChanged,
  } = useWorkflowContext();

  const initialIsEntities =
    selectedNode?.capture?.isEntities === true ||
    (Array.isArray(selectedNode?.capture?.suggestedEntityQuestions) &&
      selectedNode?.capture?.suggestedEntityQuestions.length > 0) ||
    selectedNode?.capture?.selectedTab === '1' ||
    selectedNode?.selectedTab === '1';

  const initialSelectedTab = initialIsEntities
    ? '1'
    : selectedNode?.capture?.selectedTab || selectedNode?.selectedTab || '0';

  const normalizeValuesForSave = (values) => {
    const next = { ...values };

    next.selectedTab = next.isEntities ? '1' : next.selectedTab;
    next.isEntities = next.selectedTab === '1';

    if (next.selectedTab === '1') {
      next.variableId = null;
      next.variableLabel = '';
      next.reassessIntent = false;
      next.hasSuggestions = false;
      next.hasAIGenerated = false;
      next.suggestedQuestions = [];
    } else {
      next.entities_Reprompt = false;
      next.entities_MaxRetries = 1;
      next.entities_FormName = '';
      next.suggestedEntityQuestions = [];
    }
    next.suggestedEntityQuestions = (next.suggestedEntityQuestions || []).map(
      (q) => ({
        ...q,
        lookups: Array.isArray(q.lookups)
          ? q.lookups
              .filter(
                (l) =>
                  String(l?.key || '').trim() || String(l?.value || '').trim(),
              )
              .map((l) => ({
                id: l?.id || null,
                captureNodeEntityQuestionId:
                  l?.captureNodeEntityQuestionId || q?.id || null,
                key: l?.key || '',
                value: l?.value || '',
              }))
          : [],
      }),
    );
    return next;
  };

  const computeIsError = (values) => {
    if (values.selectedTab === '1') {
      const list = values.suggestedEntityQuestions || [];
      return list.length === 0;
    }

    return !Number.isInteger(values.variableId);
  };

  const formik = useFormik({
    initialValues: {
      selectedTab: initialSelectedTab,

      captureType: selectedNode?.captureType || '0',
      entitiesType: selectedNode?.entitiesType || '0',

      variableId:
        selectedNode?.capture?.variableId === 0 ||
        selectedNode?.capture?.variableId
          ? Number(selectedNode?.capture?.variableId)
          : null,

      reassessIntent: selectedNode?.capture?.reassessIntent || false,
      hasSuggestions: selectedNode?.capture?.hasSuggestions || false,
      hasAIGenerated: selectedNode?.capture?.hasAIGenerated || false,

      isEntities: initialIsEntities,

      entities_Reprompt: selectedNode?.capture?.entities_Reprompt || false,
      entities_MaxRetries: selectedNode?.capture?.entities_MaxRetries || 1,
      entities_FormName: selectedNode?.capture?.entities_FormName || '',

      suggestedQuestions: selectedNode?.capture?.suggestedQuestions?.map(
        (q) => ({
          question: q.question || '',
        }),
      ) || [{ question: '' }],

      suggestedEntityQuestions:
        selectedNode?.capture?.suggestedEntityQuestions?.map((q) => ({
          variableId:
            q?.variableId === 0 || q?.variableId ? Number(q.variableId) : null,
          question: q?.question || '',
          validationRule: q?.validationRule || '',

          validation_ErrorMessage: q?.validation_ErrorMessage || '',
          orderNo: q?.orderNo || 0,
          isLookupEnabled: Boolean(q?.isLookupEnabled),
          lookups: Array.isArray(q?.lookups)
            ? q.lookups.map((l) => ({
                id: l?.id || null,
                captureNodeEntityQuestionId:
                  l?.captureNodeEntityQuestionId || null,
                key: l?.key || '',
                value: l?.value || '',
              }))
            : [],
        })) || [],
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      selectedTab: Yup.string().required(),
      variableId: Yup.number()
        .nullable()
        .integer()
        .when('selectedTab', {
          is: '0',
          then: (schema) => schema.required('variable Type is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
    onSubmit: (values) => {
      const initialNode = initialWorkflowData?.find((n) => n?.id === id);
      if (!initialNode) return;

      const initialCapture = initialNode?.capture || {};
      const initialNormalized = normalizeValuesForSave({
        ...formik.initialValues,
        ...initialCapture,
        selectedTab:
          initialCapture?.selectedTab ||
          initialNode?.selectedTab ||
          formik.initialValues.selectedTab,
        isEntities:
          initialCapture?.isEntities ??
          (initialCapture?.selectedTab === '1' ||
            initialNode?.selectedTab === '1'),
      });

      const currentNormalized = normalizeValuesForSave(values);

      const changed = !isEqual(currentNormalized, initialNormalized);

      setIsChanged((prev) => {
        const alreadyExists = prev?.includes(id);

        if (changed && !alreadyExists) return [...prev, id];
        if (!changed && alreadyExists)
          return prev?.filter((nodeId) => nodeId !== id);

        return prev;
      });
    },
  });

  const handleSubmit = async () => {
    try {
      const errors = await formik.validateForm();
      if (Object.keys(errors || {}).length) {
        formik.setTouched({ selectedTab: true, variableId: true }, true);
      }

      const valuesToSave = normalizeValuesForSave(formik.values);
      const nextIsError = computeIsError(valuesToSave);

      setTimeout(() => {
        const updatedNodes = workflowData?.nodes?.map((node) =>
          node.id === id
            ? {
                ...node,
                selectedTab: valuesToSave.selectedTab,
                capture: valuesToSave,
                is_Error: nextIsError,
              }
            : node,
        );

        setWorkflowData((prev) => ({
          ...prev,
          nodes: updatedNodes,
        }));

        onClose();
        formik.resetForm();
      }, 100);
    } catch {
      toast.error('Something went wrong');
    }
  };

  useEffect(() => {
    const updatedNodes = workflowData?.nodes?.map((node) =>
      node.id === id ? { ...node, is_Error: isError } : node,
    );

    setWorkflowData((prev) => ({
      ...prev,
      nodes: updatedNodes,
    }));
  }, [isError]);

  return (
    <RightSlider
      title={title}
      action={{ isOpen, setIsOpen: handleSubmit }}
      className={styles['menu-container']}
    >
      <FormikProvider value={formik}>
        <CaptureForm data={data} isFetching={isFetching} />
      </FormikProvider>
    </RightSlider>
  );
};

export default CaptureModal;
